import React, { FC, ReactElement, useCallback, useEffect, useRef, useState } from "react"
import SyncableModel from "../../database/SyncableModel"
import {
  Alert,
  Appearance,
  Platform,
  ScrollView,
  TouchableOpacity,
  TouchableOpacityProps,
  ViewStyle,
} from "react-native"
import { Button, FormProps, JalaLoading, Screen, Text, useRouteName } from "../../components"
import { FormikProps, FormikValues } from "formik"
import { Modal, useDisclose, View } from "native-base"
import { color, nativeBaseTheme } from "../../theme"
import { goBack } from "../../navigators"
import { observer } from "mobx-react-lite"
import _ from "lodash"
import { translate, TxKeyPath } from "../../i18n"
import JalaAnalytic from "../../utils/jala-analytic"
import { TextProps } from "../text/text.props"

export interface ModelCreateScreenProps {
  ModelForm: FC<FormProps>,
  modelFormProps?: FormProps,
  modelStore: any,
  model?: SyncableModel,
  colorScheme?: "light" | "dark",
  initialValues?: Record<string, any>
  onSubmit?: (data: any) => void
  onSuccess?: (success: boolean, model?: SyncableModel, state?: "store"|"update"|"delete") => boolean
  ignoreDirty?: boolean
  hideOnBlurred?: boolean
  saveTx?: TxKeyPath

  renderDeleteButton?: ({ model, colorScheme, loading, handleDelete }) => React.ReactElement,
  deleteModalTitle?: string
  deleteModalSubtitle?: string
  visible?: boolean
  denyDelete?: boolean,
  doubleButton?: boolean,
  leftTx?: TxKeyPath,
  rightTx?: TxKeyPath,

  scrollViewRef?: React.MutableRefObject<ScrollView>

  HeaderComponent?: ReactElement | null
}

const ROOT: ViewStyle = {
  flexGrow: 1
}

const SPACE: ViewStyle = {
  flex: 1,
}

const BUTTON_CONTAINER: ViewStyle = {
  borderTopWidth: 1,
  borderTopColor: color.palette.gray300,
}

const DELETE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginVertical: 8,
  marginHorizontal: 16
}

const BUTTON: ViewStyle = {
  marginVertical: 8,
  marginHorizontal: 16
}

const BUTTON_DETAIL_ITEM: ViewStyle = {
  alignSelf: "center",
  paddingVertical: 6,
  paddingHorizontal: 16,
  borderRadius: 38
}

const DOUBLE_BUTTON: ViewStyle = {
  width: "45%"
}

export const DeleteModal: FC<{
  colorScheme?: "light" | "dark",
  onConfirm?: () => void,
  disabled?: boolean,
  deleteModalTitle?: string,
  deleteModalTitleProps?: TextProps,
  deleteModalSubtitle?: string,
  deleteModalSubtitleProps?: TextProps,
  colorTitle?: string
} & Omit<TouchableOpacityProps, "onPress">> = observer(function DeleteModal(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    children,
    onConfirm: onConfirmProp,
    deleteModalTitle, deleteModalTitleProps,
    deleteModalSubtitle, deleteModalSubtitleProps,
    colorTitle,
    ...touchableOpacityProps
  } = props;
  const { isOpen, onClose, onOpen } = useDisclose()

  const onConfirm = useCallback(() => {
    onConfirmProp && onConfirmProp()
    onClose()
  }, [onClose, onConfirmProp])

  return (<>
    <TouchableOpacity onPress={onOpen} {...touchableOpacityProps}>
      {children}
    </TouchableOpacity>
    <Modal isOpen={isOpen} onClose={onClose}>
      <View
        alignSelf="stretch"
        marginY="80px"
        marginX="28px"
        paddingY="24px"
        paddingX="16px"
        rounded="16px"
        backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
      >
        <View alignItems="center" marginBottom="12px">
          <Text
            text={deleteModalTitle || translate("modelCreateScreen.areYouSure")}
            transformText={_.upperFirst}
            preset="title1"
            colorScheme={colorScheme}
            marginY="8px" marginX="7px"
            color={colorTitle}
            {...deleteModalTitleProps}
          />
          <Text
            text={deleteModalSubtitle || translate('modelCreateScreen.areYouSureYouWantToSomething', {
              something: translate('modelCreateScreen.deleteSomething', {
                something: translate('modelCreateScreen.thisData')
              })
            })}
            transformText={_.upperFirst}
            preset="body2"
            colorScheme={colorScheme}
            marginBottom="8px"
            {...deleteModalSubtitleProps}
          />
        </View>
        <View flexDirection="row" justifyContent="center">
          <Button
            testID="jala:id/delete-yes-button"
            text={translate('Yes, I\'m Sure').toUpperCase()}
            preset="outline"
            textPreset="title4"
            colorScheme={colorScheme}
            style={BUTTON_DETAIL_ITEM}
            onPress={onConfirm}
          />
          <View width="12px" />
          <Button
            testID="jala:id/delete-no-button"
            text={_.upperCase(translate('No'))}
            preset="primary"
            textPreset="title4"
            colorScheme={colorScheme}
            style={BUTTON_DETAIL_ITEM}
            onPress={onClose}
          />
        </View>
        {/* <Text */}
        {/*   text={`${moment(treated_at).format('DD MMM YYYY')} (${age})`} */}
        {/*   preset="body2" */}
        {/*   colorScheme={colorScheme} */}
        {/*   marginBottom="8px" */}
        {/* /> */}
        {/* <ScrollView */}
        {/*   contentContainerStyle={SCROLL_VIEW_DETAIL_ITEM} */}
        {/* > */}
        {/*   <Text text={remark || '-'} preset="body2" colorScheme={colorScheme} /> */}
        {/* </ScrollView> */}
        {/* <Button */}
        {/*   testID="jala:id/ok-button" */}
        {/*   tx="common.ok" */}
        {/*   preset="primary" */}
        {/*   colorScheme={colorScheme} */}
        {/*   style={BUTTON_DETAIL_ITEM} */}
        {/*   onPress={onClose} */}
        {/* /> */}
      </View>
    </Modal>
  </>)
})

export const ModelCreateScreen: FC<ModelCreateScreenProps> = observer(function ModelCreateScreen(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    ModelForm, modelFormProps,
    modelStore,
    model = null,
    initialValues,
    onSubmit: onSubmitProps,
    onSuccess,
    ignoreDirty,
    hideOnBlurred = true,
    saveTx,
    renderDeleteButton,
    deleteModalTitle, deleteModalSubtitle,
    denyDelete = false,
    doubleButton = false,
    leftTx,
    rightTx,

    scrollViewRef: scrollViewRefProps,

    HeaderComponent
  } = props;

  const [ isError, setIsError ] = useState<boolean>(true)

  const { routeNameRef, previousRouteNameRef } = useRouteName()

  const scrollViewRef = useRef<ScrollView>(null);

  const { loading, error: modelError } = modelStore || {};

  const modelForm = useRef<FormikProps<FormikValues>>()
  const modelFormRef = useCallback((node) => {
    modelForm.current = node;
    if (modelForm.current) setIsError(!((ignoreDirty || modelForm.current.dirty) && modelForm.current.isValid))
  }, [ignoreDirty])

  useEffect(() => {
    if (!modelStore) return;

    modelStore.init()
  }, [modelStore])

  useEffect(() => {
    if (!modelStore) return;
    if (modelError) {
      if (modelError.response && modelError.response.status === 422) {
        const errors = {}
        Object.keys(modelError.response.data.errors).forEach((key) => {
          errors[key] = (modelError.response.data.errors[key] || [])[0]
        })
        if (modelForm.current) modelForm.current.setErrors(errors)
        modelStore.set('error', null)
      }
      else {
        if (Platform.OS === 'web') {
          if (modelError.response)
            confirm(modelError.response.originalError.message)
          modelStore.set('error', null)
        }
        else
          Alert.alert(
            'Error',
            modelError.message,
            [ { onPress: () => modelStore.set('error', null) } ]
          );
      }
    }
  }, [modelError, modelStore])

  const onSubmit = useCallback(async (data) => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = !model ? await modelStore.store(data) : await modelStore.update(model, data)

    JalaAnalytic.log("Mobile app: Form On Submit", {
      form: ModelForm.displayName || 'Unknown',
      data: JSON.stringify(data || null),
      success,
      routeName: routeNameRef.current,
      previousRouteName: previousRouteNameRef.current
    })

    if ((!onSuccess || onSuccess(success, model, model ? 'update' : 'store')) && success) {
      goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [ModelForm.displayName, model, modelStore, onSuccess, previousRouteNameRef, routeNameRef])

  const handleSubmit = useCallback((event) => {
    if (modelForm.current) modelForm.current.handleSubmit(event)
  }, [])

  const handleCancel = useCallback(() => {
    goBack('')
  }, [])

  const handleDelete = useCallback(async () => {
    if (!modelStore) return;
    modelStore.set('loading', true)
    const success = await modelStore.delete(model)

    JalaAnalytic.log("Mobile app: Form Handle Delete", {
      form: ModelForm.displayName || 'Unknown',
      success
    })

    if ((!onSuccess || onSuccess(success, model, 'delete')) && success) {
      goBack('Home.Stack')
    }
    modelStore.set('loading', false)
  }, [ModelForm.displayName, model, modelStore, onSuccess])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={hideOnBlurred}
      scrollViewRef={(instance) => {
        scrollViewRef.current = instance;
        if (scrollViewRefProps) scrollViewRefProps.current = instance
      }}
    >
      {HeaderComponent}
      <ModelForm
        initialValues={initialValues}
        colorScheme={colorScheme}
        innerRef={modelFormRef}
        onSubmit={onSubmitProps || onSubmit}
        parentScrollViewRef={scrollViewRef}
        {...modelFormProps}
      />
      <View style={SPACE} />
      <View style={BUTTON_CONTAINER}>
        {renderDeleteButton && renderDeleteButton({ model, colorScheme, loading, handleDelete })
          || (!denyDelete && model && <DeleteModal
            testID="jala:id/delete-button"
            style={DELETE_BUTTON}
            onConfirm={handleDelete}
            deleteModalTitle={deleteModalTitle}
            deleteModalSubtitle={deleteModalSubtitle}
            disabled={loading}
          >
            <Text
              tx="delete"
              preset="title4"
              colorScheme={colorScheme}
              color={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
            />
          </DeleteModal>)}
        {doubleButton ?
          (<View flexDirection="row" alignItems="center" padding="16px" justifyContent="space-between">
            <Button
              testID="jala:id/left-button"
              tx={leftTx || "cancel"}
              colorScheme={colorScheme}
              loading={loading}
              onPress={handleCancel}
              style={DOUBLE_BUTTON}
              preset="outline"
            />
            <Button
              testID="jala:id/right-button"
              tx={rightTx || "save"}
              colorScheme={colorScheme}
              disabled={isError}
              loading={loading}
              onPress={handleSubmit}
              style={DOUBLE_BUTTON}
            />
          </View>) :
          <Button
            testID="jala:id/save-button"
            tx={saveTx || "save"}
            colorScheme={colorScheme}
            disabled={isError}
            loading={loading}
            onPress={handleSubmit}
            style={BUTTON}
          />
        }
      </View>
      <Modal isOpen={loading}>
        <View
          width="72px" height="72px"
          alignItems="center"
          justifyContent="center"
          rounded="48px"
          backgroundColor="rgba(255, 255, 255, 0.75)"
        >
          <JalaLoading />
        </View>
      </Modal>
    </Screen>
  )
})
